import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { useTheme } from '@/app/ThemeContext';
import { TabOption } from '@/components/chariot-ui';
import { NoInformation as NoInformationIcon } from '@/components/chariot-ui';
import { CopyToClipboard } from '@/components/CopyToClipboard';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { HOTKEY_SCOPES } from '@/hooks/hotkeys';
import { PoeContent as Content } from '@/sections/poe/components/PoeContent';
import { usePOEData } from '@/sections/poe/hooks/usePOEData';
import { usePOESections } from '@/sections/poe/hooks/usePOESections';
import type {
  ExtendedTabOption,
  POEProps,
  SectionContent,
} from '@/sections/poe/types/poe.types';
import { TabDescription } from '@/sections/TabDescription';
import { useGlobalState } from '@/state/global.state';
import { cn } from '@/utils/classname';

const getSectionContent = (
  sections: Record<string, SectionContent>,
  label: string | React.ReactNode,
  copyContents?: Record<string, string>
): string | undefined => {
  if (typeof label !== 'string') return undefined;

  if (copyContents && copyContents[label]) {
    return copyContents[label];
  }

  const content = sections[label];
  if (!content) return undefined;

  if (typeof content === 'string') {
    return content;
  }
  if (Array.isArray(content)) {
    return content.join('\n');
  }
  if (React.isValidElement(content)) {
    return undefined;
  }
  return JSON.stringify(content);
};

const getDisplayContent = (
  sections: Record<string, SectionContent>,
  label: string | React.ReactNode
): SectionContent | undefined => {
  if (typeof label !== 'string') return undefined;
  return sections[label];
};

export function POE(props: POEProps) {
  const {
    poeLocation,
    fallbackPOELocation,
    severity,
    name,
    source,
    attributes = [],
    className,
    riskKey,
    webpage,
    patches,
    verificationFallbackValue,
    onExitPoeMode,
  } = props;

  const { poeMode } = useGlobalState();
  const { currentTheme } = useTheme();

  const { file, fileStatus, evidenceFileValue, evidenceLoadFailed } =
    usePOEData({
      riskKey,
      poeLocation,
      fallbackPOELocation,
      name,
    });

  const [selectedTabState, setSelectedTab] = useState<string | null>(null);

  // Rail defaults to EXPANDED on mount (icon + label, 168px) so the
  // Detection/Verification navigation is legible on first paint; users can
  // collapse it to an icon-only 52px strip to reclaim proof body width. Not
  // persisted: the collapse choice lives only for the life of this POE
  // instance. VulnerabilityDrawer keys POE by poeLocation, so each risk
  // remounts and re-opens expanded; a reused instance (e.g. TechnologyEvidence
  // switching asset/port) keeps whatever state the user last set.
  const [railCollapsed, setRailCollapsed] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  const sectionsRef = useRef<HTMLDivElement>(null);

  const handleCopy = useCallback((content: string) => {
    navigator.clipboard.writeText(content).catch(console.error);
  }, []);

  const { sections, availableTabs, copyContents } = usePOESections({
    name,
    source,
    severity,
    file,
    fileStatus,
    evidenceFileValue,
    evidenceLoadFailed,
    attributes,
    handleCopy,
    webpage,
    theme: currentTheme,
    riskKey,
    patches,
    verificationFallbackValue,
  });

  // Default landing tab: the first tab that actually has sub-tab content, so an
  // empty ATTRIBUTES entry (retained in `availableTabs` for rail parity but with
  // no sub-tabs) never becomes the default view while a content-bearing tab
  // (e.g. VERIFICATION) exists. Falls back to availableTabs[0] when nothing has
  // content, preserving the empty-state fallback. Referentially stable across
  // identity-only re-renders (it indexes into the memoized `availableTabs`).
  const defaultTab = (() => {
    if (source.startsWith('constantine')) {
      const demonstrated = availableTabs.find(
        tab => tab.value === 'demonstrated' && (tab.subTabs?.length ?? 0) > 0
      );
      if (demonstrated) return demonstrated;
    }
    return (
      availableTabs.find(tab => (tab.subTabs?.length ?? 0) > 0) ??
      availableTabs[0]
    );
  })();
  // Rehydrate the selected tab from the CURRENT availableTabs by matching the
  // selected top-level VALUE, so selectedTab always carries the FRESH object
  // (hence fresh `.subTabs`) when the same top-level tab stays selected but a
  // data refetch adds/removes per-finding sub-tabs (DETECTION, or VERIFICATION
  // when riskKey is set — see constantine.processor `${tabId}_verification`).
  // `selectedTabState` is value-memory only (which top-level tab the user
  // picked); its frozen object is never the render source. Empty→prose is
  // untouched: that flip changes neither the top-level value nor the sub-tab
  // SET — only sections['verification_analysis'] content, already looked up
  // fresh by value at the sub-tab map below.
  const selectedTab =
    availableTabs.find(tab => tab.value === selectedTabState) ?? defaultTab;

  const scrollToSection = (sectionId?: string) => {
    if (!sectionId || !sectionsRef.current) return;
    const sectionElement = sectionsRef.current.querySelector(
      `[data-section="${sectionId}"]`
    );
    sectionElement?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleTabChange = (tab: ExtendedTabOption) => {
    setSelectedTab(tab.value);
    scrollToSection(tab.value);
    poeMode.setEnabled(true);
  };

  // Scroll the selected section into view. Keyed on the selectedTab VALUE (a
  // stable string), not the selectedTab OBJECT — identity-only churn of
  // `availableTabs[0]` must not re-run this effect (the React-#185 loop guard,
  // T29). Separated from keyboard registration, which is now declarative below.
  useEffect(() => {
    if (selectedTab?.value && name) scrollToSection(selectedTab.value);
  }, [selectedTab?.value, name]);

  // ── POE-mode keyboard navigation (ENG-43) ──────────────────────────────────
  //
  // Migrated off the legacy global keyboard bus onto react-hotkeys-hook. The
  // library holds the handler in a ref (always the latest closure) and owns the
  // listener lifecycle, so there is NO add/remove dance on identity churn of
  // `availableTabs`/`selectedTab` — the React-#185 infinite-render-loop hazard is
  // structurally eliminated: no `setListeners`/`setState` runs in the binding
  // path, so that identity churn can't cascade through GlobalStateProvider's
  // ~110 consumers. The 4th-arg deps are the library's own callback-memoization
  // key (not an effect dep list) — for the index-input deps they merely refresh
  // the ref'd closure, with NO re-registration on identity churn, so consecutive
  // presses keep cycling (T28) while T29's identity-only re-renders never re-run
  // anything. (The `enabled`-gated bindings below DO rebind when `poeMode.enabled`
  // toggles — but that is a plain addEventListener add/remove swap with no
  // setState, so it stays loop-safe.)
  //
  // Scope `overlay`: POE lives in a drawer and is never globally suppressed.

  // `p` → exit POE mode (the EXIT half of the single `p` toggle). This is one of
  // two mutually-exclusive `p` handlers: this exit handler is enabled ONLY while
  // POE mode is on, and the matching ENTER handler (in useVulnerabilityKeyboard)
  // is enabled ONLY while POE mode is off — so exactly one is ever armed and a
  // single `p` press can never double-fire. Replaces the old Alt+ArrowLeft exit.
  useHotkeys(
    'p',
    () => {
      poeMode.setEnabled(false);
      onExitPoeMode?.();
    },
    {
      enabled: poeMode.enabled,
      scopes: [HOTKEY_SCOPES.overlay],
      enableOnFormTags: false,
      preventDefault: true,
    },
    [poeMode.enabled, onExitPoeMode]
  );

  // Alt+ArrowDown → next tab (wrap). Gated on poeMode.enabled.
  useHotkeys(
    'alt+down',
    () => {
      const foundIndex = availableTabs.findIndex(
        tab => tab.value === selectedTab?.value
      );
      const currentTabIndex = foundIndex === -1 ? 0 : foundIndex;
      const nextTabIndex =
        currentTabIndex === availableTabs.length - 1 ? 0 : currentTabIndex + 1;
      const nextTab = availableTabs[nextTabIndex];
      if (nextTab) handleTabChange(nextTab);
    },
    {
      enabled: poeMode.enabled,
      scopes: [HOTKEY_SCOPES.overlay],
      enableOnFormTags: false,
      preventDefault: true,
    },
    [availableTabs, selectedTab?.value, poeMode.enabled]
  );

  // Alt+ArrowUp → previous tab (wrap). Gated on poeMode.enabled.
  useHotkeys(
    'alt+up',
    () => {
      const foundIndex = availableTabs.findIndex(
        tab => tab.value === selectedTab?.value
      );
      const currentTabIndex = foundIndex === -1 ? 0 : foundIndex;
      const previousTab =
        currentTabIndex === 0
          ? availableTabs[availableTabs.length - 1]
          : availableTabs[currentTabIndex - 1];
      if (previousTab) handleTabChange(previousTab);
    },
    {
      enabled: poeMode.enabled,
      scopes: [HOTKEY_SCOPES.overlay],
      enableOnFormTags: false,
      preventDefault: true,
    },
    [availableTabs, selectedTab?.value, poeMode.enabled]
  );

  // On unmount, clear POE mode so the table p-enter is re-armed for re-entry.
  useEffect(() => {
    return () => {
      poeMode.setEnabled(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only cleanup
  }, []);

  const hasContent =
    availableTabs.length > 0 && Object.keys(sections).length > 0;

  // Stable primitive key of the tab SET. Recomputed identically each render, so
  // it only changes when the actual set of tab values changes — unlike the
  // `availableTabs` array identity, adding it to the arm/validate deps below
  // cannot reintroduce the T29 render loop, but it DOES re-fire the validity
  // check when poeLocation swaps the tab set (stale-tab regression).
  const tabSetKey = availableTabs.map(t => t.value).join('|');

  useEffect(() => {
    if (hasContent) {
      // Arm POE mode whenever a valid POE view is rendered, so the `p` toggle's
      // exit half is reachable however the drawer was opened (not just when the
      // selected tab is stale). `setEnabled` is an idempotent useState setter,
      // so re-arming while already true is a no-op.
      poeMode.setEnabled(true);
      // Validate the STORED selection (value-memory), NOT the derived
      // `selectedTab` — the derived value is always valid because of the
      // `?? defaultTab` fallback in the render, so a check against it would
      // never fire (dead code). If the user-selected tab's value is gone from
      // availableTabs, CLEAR the stored selection so the render falls to
      // defaultTab AND a later refetch that reintroduces that value cannot
      // teleport the UI back to it without a fresh user click.
      if (
        selectedTabState != null &&
        !availableTabs.some(tab => tab.value === selectedTabState)
      ) {
        setSelectedTab(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- poeMode.setEnabled is a stable setter; deps are intentionally the STABLE hasContent + STORED selection value (selectedTabState, the value-memory the check reads) + tabSetKey (a recomputed-identical primitive, NOT availableTabs/sections identity) to keep the T29 render-loop guard flat while still re-validating on tab-set swaps
  }, [hasContent, selectedTabState, tabSetKey]);

  if (fileStatus === 'pending') {
    return (
      <Skeleton shape="block" className="m-4 min-h-48 w-[calc(100%-32px)]" />
    );
  }

  if (!file && availableTabs.length === 0) {
    return (
      <div className="my-4 flex flex-col items-center justify-center">
        <NoInformationIcon className="text-chariot-stroke mb-4" />
        <p className="text-chariot-text-primary text-lg font-bold">
          No Proof of Exploit Available
        </p>
      </div>
    );
  }

  return (
    <>
      <div
        className={cn('flex h-full overflow-hidden', className)}
        data-testid="evidence-tab"
      >
        <div className="flex w-full gap-4">
          {/* Collapsible rail. Width animates between collapsed (icon-only,
              52px) and expanded (icon + label, 168px); reduced-motion makes
              the change instant. The Tabs instance stays mounted across the
              animation so the active tab selection state is preserved and the
              rail does not remount/flicker mid-transition. (POE keyboard nav is
              handled separately via react-hotkeys-hook, independent of mount.) */}
          <motion.div
            className="border-chariot-stroke shrink-0 overflow-x-hidden overflow-y-auto border-r py-4"
            initial={false}
            animate={{ width: railCollapsed ? 52 : 168 }}
            transition={
              prefersReducedMotion
                ? { duration: 0 }
                : { type: 'tween', duration: 0.2, ease: 'easeInOut' }
            }
          >
            <button
              type="button"
              onClick={() => setRailCollapsed(prev => !prev)}
              aria-expanded={!railCollapsed}
              aria-label={railCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className={cn(
                'text-chariot-text-secondary hover:bg-chariot-background-secondary hover:text-chariot-text-primary focus-visible:ring-chariot-brand-primary mb-2 flex h-10 items-center rounded-md transition-colors focus-visible:ring-2 focus-visible:outline-hidden',
                railCollapsed ? 'mx-auto w-10 justify-center' : 'ml-auto w-10'
              )}
            >
              <ChevronLeft
                className={cn(
                  'size-4 transition-transform duration-200',
                  railCollapsed && 'rotate-180'
                )}
              />
            </button>
            <TooltipProvider delayDuration={150}>
              <Tabs
                value={(selectedTab || availableTabs[0])?.value}
                onValueChange={value => {
                  const tab = availableTabs.find(t => t.value === value);
                  if (tab) handleTabChange(tab);
                }}
                orientation="vertical"
              >
                <TabsList
                  variant="boxed"
                  orientation="vertical"
                  className="gap-2"
                >
                  {availableTabs.map(tab => {
                    const labelText =
                      typeof tab.label === 'string' ? tab.label : undefined;
                    const Icon = tab.icon;
                    const trigger = (
                      <TabsTrigger
                        key={tab.value}
                        value={tab.value}
                        variant="boxed"
                        orientation="vertical"
                        // Crimson brand accent for the active item; standard
                        // focus token. Overrides the boxed variant's neutral
                        // active border/bg.
                        className={cn(
                          'focus-visible:ring-chariot-brand-primary h-11 gap-3 border-transparent capitalize focus-visible:ring-2',
                          'data-[state=active]:border-chariot-brand-primary data-[state=active]:bg-chariot-brand-primary/10 data-[state=active]:text-chariot-brand-primary',
                          railCollapsed && 'justify-center px-0'
                        )}
                        // Collapsed icon-only triggers hide their visible label,
                        // so give them a programmatic accessible name. When
                        // expanded the inline label provides the name, so no
                        // aria-label (and no duplicate-label tooltip).
                        aria-label={railCollapsed ? labelText : undefined}
                      >
                        {Icon ? <Icon className="size-4 shrink-0" /> : null}
                        {!railCollapsed && (
                          <span className="truncate">{tab.label}</span>
                        )}
                      </TabsTrigger>
                    );

                    // Tooltip only in the collapsed state (the visible label is
                    // hidden then). Showing it while expanded would duplicate
                    // the inline label. Radix Tooltip fires on hover AND
                    // keyboard focus, satisfying both affordances.
                    return railCollapsed && labelText ? (
                      <Tooltip key={tab.value}>
                        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
                        <TooltipContent side="right">
                          {labelText}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      trigger
                    );
                  })}
                </TabsList>
              </Tabs>
            </TooltipProvider>
          </motion.div>

          <div className="flex-1 overflow-y-auto py-4 pr-4" ref={sectionsRef}>
            {selectedTab && (
              <div className="flex flex-col gap-6">
                {!selectedTab.hideHeader && (
                  <div className="border-chariot-stroke pb-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-chariot-text-primary text-xl font-semibold">
                        {selectedTab.heading}
                      </h2>
                    </div>
                    <p className="text-chariot-text-secondary mt-1 text-sm">
                      <TabDescription tabId={selectedTab.value} />
                    </p>
                  </div>
                )}

                <div className="flex flex-col gap-4">
                  {selectedTab.subTabs?.map((subTab: TabOption) => {
                    const copyContent = getSectionContent(
                      sections,
                      subTab.value,
                      copyContents
                    );
                    const displayContent = getDisplayContent(
                      sections,
                      subTab.value
                    );
                    return displayContent ? (
                      <div
                        key={subTab.value}
                        data-section={subTab.value}
                        className="border-chariot-stroke bg-chariot-background-primary overflow-hidden rounded-md border"
                      >
                        <div className="border-chariot-stroke flex items-center justify-between border-b px-4 py-3">
                          <h3 className="text-chariot-text-primary flex items-center gap-2 font-semibold">
                            {subTab.value === 'matched_at' ? (
                              <CheckCircleIcon className="text-chariot-stroke-info size-6" />
                            ) : null}
                            <span>{subTab.label}</span>
                          </h3>
                          {copyContent && (
                            <CopyToClipboard
                              textToCopy={copyContent}
                              alwaysShowCopyIcon
                              iconClassName="size-4 text-chariot-text-secondary"
                            />
                          )}
                        </div>
                        <div className="p-4">
                          <Content content={displayContent} />
                        </div>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
